import type { TenantRole } from '@/types/user';
import { ROLE_LEVELS } from '@/types/user';

interface RoleOption {
  role: TenantRole;
  title: string;
  desc: string;
  icon: string;
  restaurantOnly?: boolean;
}

// CAPTAIN is deliberately absent: it is reserved but not implemented (FR-AUTH-009).
// The API DTO, the shared TenantRole type and the users.role CHECK constraint all
// reject it, so offering it here could only ever produce an error (QA 2.7.6).
const ROLES: RoleOption[] = [
  {
    role: 'OWNER',
    title: 'Owner',
    desc: 'Full business access, billing, settings, user management',
    icon: 'M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4',
  },
  {
    role: 'MANAGER',
    title: 'Manager',
    desc: 'Sales insights, user management, location access',
    icon: 'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z',
  },
  {
    role: 'CASHIER',
    title: 'Cashier',
    desc: 'POS billing, customer lookup, shift management',
    icon: 'M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z',
  },
];

/** Roles the picker offers for the given business type, caller level and mode. */
export function availableRoles({
  businessType = 'RETAIL',
  currentRoleLevel = 50,
  mode = 'edit',
}: Pick<RoleSelectorProps, 'businessType' | 'currentRoleLevel' | 'mode'>): RoleOption[] {
  return ROLES.filter((r) => {
    // OWNER is only assignable when editing, not when creating (per FORMS.md §3.1)
    if (mode === 'create' && r.role === 'OWNER') return false;
    if (r.restaurantOnly && businessType !== 'RESTAURANT') return false;
    const targetLevel = ROLE_LEVELS[r.role] ?? 0;
    return targetLevel < currentRoleLevel;
  });
}

interface RoleSelectorProps {
  value?: TenantRole;
  onChange: (role: TenantRole) => void;
  businessType?: 'RETAIL' | 'RESTAURANT';
  currentRoleLevel?: number;
  disabled?: boolean;
  /** 'create' excludes OWNER per FORMS.md §3.1; 'edit' allows OWNER per §3.2 */
  mode?: 'create' | 'edit';
}

export function RoleSelector({
  value,
  onChange,
  businessType = 'RETAIL',
  currentRoleLevel = 50,
  disabled,
  mode = 'edit',
}: RoleSelectorProps) {
  const available = availableRoles({ businessType, currentRoleLevel, mode });

  return (
    <div
      role="radiogroup"
      aria-label="Select user role"
      className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3"
    >
      {available.map((opt) => {
        const isSelected = value === opt.role;
        return (
          <button
            key={opt.role}
            type="button"
            role="radio"
            aria-checked={isSelected}
            aria-label={`${opt.title}: ${opt.desc}`}
            disabled={disabled}
            onClick={() => onChange(opt.role)}
            className={[
              'relative flex flex-col items-start gap-2 rounded-2xl p-4 text-left transition-all duration-200',
              isSelected
                ? 'border-2 border-indigo-500 bg-indigo-50/60 shadow-[0_8px_20px_-12px_rgba(99,102,241,0.4)]'
                : 'border-2 border-slate-200/80 bg-white hover:border-slate-300 hover:shadow-md',
              disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
            ].join(' ')}
          >
            {isSelected && (
              <span
                className="absolute top-2.5 right-2.5 flex h-5 w-5 items-center justify-center rounded-full bg-indigo-600 text-white"
                aria-hidden="true"
              >
                <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                  <path d="M5 13l4 4L19 7" />
                </svg>
              </span>
            )}
            <div
              className={[
                'flex h-9 w-9 items-center justify-center rounded-xl',
                isSelected ? 'bg-indigo-100 text-indigo-600' : 'bg-slate-100 text-slate-400',
              ].join(' ')}
            >
              <svg
                className="h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={1.5}
                aria-hidden="true"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d={opt.icon} />
              </svg>
            </div>
            <div>
              <p
                className={[
                  'text-sm font-semibold',
                  isSelected ? 'text-indigo-700' : 'text-slate-700',
                ].join(' ')}
              >
                {opt.title}
              </p>
              <p className="mt-0.5 text-xs text-slate-500">{opt.desc}</p>
              {opt.restaurantOnly && (
                <span className="mt-1 inline-flex rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-semibold text-amber-700">
                  Restaurant only
                </span>
              )}
            </div>
          </button>
        );
      })}
      {available.length === 0 && (
        <p className="col-span-full text-sm text-slate-400">
          No roles available to assign at your permission level.
        </p>
      )}
    </div>
  );
}
